import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react"
import {
    FILTER_CHIP_SELECT_CLASS,
    FILTER_CHIP_SELECT_COMMAND_CLASS,
    filterChipLabelStyle,
    filterChipValueInputStyle,
    getFilterChipCommandSelectStyle,
    getFilterChipShellStyle,
    handleFilterChipCommandBlur,
    handleFilterChipCommandFocus,
    handleFilterChipValueInputBlur,
    handleFilterChipValueInputFocus,
    measureCommandSelectWidth,
    measureValueInputWidth,
} from "./filterChipStyles.js"

const TextFilterChip = forwardRef(({
    label = "",
    displayLabel = "",
    command = "contains",
    value = "",
    isHighlighted = false,
}, ref) => {
    const [currentCommand, setCurrentCommand] = useState(command)
    const [currentValue, setCurrentValue] = useState(value)
    const [inputWidth, setInputWidth] = useState(20)
    const [commandWidth, setCommandWidth] = useState(20)
    const measureRef = useRef(null)
    const inputRef = useRef(null)

    useEffect(() => {
        setCurrentCommand(command)
        setCurrentValue(value)
    }, [command, value])

    useEffect(() => {
        setInputWidth(measureValueInputWidth(measureRef.current, value))
        setCommandWidth(measureCommandSelectWidth(measureRef.current, command))
    }, [command, value])

    function handleValueChange(newValue) {
        setCurrentValue(newValue)
        setInputWidth(measureValueInputWidth(measureRef.current, newValue))
    }

    function handleCommandChange(newCommand) {
        setCurrentCommand(newCommand)
        setCommandWidth(measureCommandSelectWidth(measureRef.current, newCommand))
        setTimeout(() => inputRef.current?.focus(), 0)
    }

    function focusValueInput() {
        inputRef.current?.focus()
    }

    useImperativeHandle(ref, () => ({
        getSearchString: () => `{${label} ${currentCommand} ${currentValue}}`,
        getChipState: () => ({
            label,
            command: currentCommand,
            value: currentValue,
        }),
        focusValueInput,
    }))

    return (
        <div
            className="flex-shrink-0"
            style={getFilterChipShellStyle(isHighlighted)}
        >
            <span style={filterChipLabelStyle}>{displayLabel || label}</span>
            <select
                className={`${FILTER_CHIP_SELECT_CLASS} ${FILTER_CHIP_SELECT_COMMAND_CLASS}`}
                value={currentCommand}
                onChange={(event) => handleCommandChange(event.target.value)}
                tabIndex={-1}
                style={getFilterChipCommandSelectStyle(commandWidth)}
                onFocus={handleFilterChipCommandFocus}
                onBlur={handleFilterChipCommandBlur}
                onMouseDown={(event) => event.stopPropagation()}
            >
                <option value="=">=</option>
                <option value="contains">contains</option>
            </select>
            <input
                ref={inputRef}
                type="text"
                data-chip-value-input
                value={currentValue}
                onChange={(event) => handleValueChange(event.target.value)}
                tabIndex={-1}
                style={{
                    ...filterChipValueInputStyle,
                    width: `${inputWidth}px`,
                }}
                onFocus={handleFilterChipValueInputFocus}
                onBlur={handleFilterChipValueInputBlur}
                onMouseDown={(event) => event.stopPropagation()}
                onKeyDown={(event) => {
                    if (event.key === "Enter") {
                        event.preventDefault()
                        event.stopPropagation()
                        const mainSearchInput = document.querySelector(
                            "[data-search-bar-container] input[type=\"text\"]:not([tabindex=\"-1\"])"
                        )
                        if (mainSearchInput) {
                            mainSearchInput.dispatchEvent(new KeyboardEvent("keydown", {
                                key: "Enter",
                                bubbles: true,
                                cancelable: true,
                            }))
                        }
                    } else if (event.key === "Escape") {
                        event.preventDefault()
                        event.stopPropagation()
                        event.target.blur()
                        event.target.closest("[data-button-index]")?.focus()
                    }
                }}
            />
            <span
                ref={measureRef}
                style={{
                    position: "absolute",
                    visibility: "hidden",
                    whiteSpace: "pre",
                    fontFamily: "inherit",
                    pointerEvents: "none",
                }}
            />
        </div>
    )
})

TextFilterChip.displayName = "TextFilterChip"

export default TextFilterChip
